import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { IFilter, IGenericObject, TableWithFields } from '@types'
import { Button, Card, Input, Select } from '@ss/meta-core-components'
import { config } from '../../config'
import { FilterIcon } from '../../assets/icons/filterIcon'
import useRest from '../../hooks/useRest'
import { generateQuery } from '@utils'

interface Props {
  setParams: React.Dispatch<React.SetStateAction<TableWithFields>>
  setOpenSidebar: () => void
  selectedIndex: string | undefined
}

/**
 * componente filtri comuni
 * @param props.SetParams {Function} setter per i filtri della pagina
 * @param props.setOpenSidebar {Function} azione del bottone 'Modifica attributi analizzati'
 * @param props.selectedIndex {string | undefined} indice selezionato
 * @component
 */
export const Filters: React.FC<Props> = (props) => {
  const { setParams, setOpenSidebar, selectedIndex } = props

  const [filterValue, setFilterValue] = useState<
    IGenericObject<IFilter | null>
  >({})

  const [count, setCount] = useState<number>()

  const { post } = useRest<{ count: number }>('/count')

  //effect per l'assegnazione dei filtri
  useEffect(() => {
    setParams((old) => ({ ...old, filters: filterValue }))
  }, [filterValue])

  //effect per la request count
  useEffect(() => {
    post({
      index: selectedIndex || config.dataSet.indexes[0].value,
      query_data: generateQuery({
        params: filterValue,
      }),
    }).then(({ count }) => {
      setCount(count)
    })
  }, [filterValue, selectedIndex])

  return (
    <Card
      width={'100%'}
      height={'134px'}
      position={'start center'}
      dataCy={'filter-template'}
    >
      <Container>
        {setOpenSidebar ? (
          <ButtonContainer>
            <Button
              onClick={() => setOpenSidebar()}
              dataCy={'open-sidebar-button'}
            >
              Modifica attributi analizzati
            </Button>
          </ButtonContainer>
        ) : (
          <></>
        )}
        <FilterContainer>
          <LabelContainer>
            <IconContainer>
              <FilterIcon />
            </IconContainer>
            <Label>Filtri</Label>
            {count ? <CountLabel>Numero righe: {count}</CountLabel> : <></>}
          </LabelContainer>
          <FilterContent>
            <InputContainer>
              <InputLabel>Regione</InputLabel>
              <Select
                dataCy={'region-select'}
                options={config.filters.regions}
                minWidth={'155px'}
                placeholder={'Seleziona'}
                onChange={(option) => {
                  setFilterValue({
                    ...filterValue,
                    regione: option
                      ? { value: `${option[0].value}`, method: 'normal' }
                      : null,
                  })
                }}
              />
            </InputContainer>
            <InputContainer>
              <InputLabel>Anno</InputLabel>
              <Select
                dataCy={'year-select'}
                options={config.filters.years}
                minWidth={'80px'}
                placeholder={'Seleziona'}
                onChange={(option) =>
                  setFilterValue({
                    ...filterValue,
                    anno: option
                      ? { value: `${option[0].value}`, method: 'normal' }
                      : null,
                  })
                }
              />
            </InputContainer>
            <InputContainer>
              <InputLabel>Mese</InputLabel>
              <Select
                dataCy={'month-select'}
                options={config.filters.month}
                minWidth={'80px'}
                placeholder={'Seleziona'}
                onChange={(option) =>
                  setFilterValue({
                    ...filterValue,
                    mese: option
                      ? { value: `${option[0].value}`, method: 'normal' }
                      : null,
                  })
                }
              />
            </InputContainer>
            <InputContainer>
              <InputLabel>Attività</InputLabel>
              <Select
                dataCy={'ateco-select'}
                placeholder={'Seleziona'}
                options={config.filters.ateco}
                onChange={(option) =>
                  setFilterValue({
                    ...filterValue,
                    sezione_attività: option
                      ? { value: `${option[0].value}`, method: 'normal' }
                      : null,
                  })
                }
              />
            </InputContainer>
            <InputContainer>
              <InputLabel>Pro Loc</InputLabel>
              <Select
                dataCy={'pro-loc-select'}
                placeholder={'Seleziona'}
                options={[
                  { value: 'true', label: 'Sedi' },
                  { value: 'false', label: 'Unità locali' },
                ]}
                onChange={(option) =>
                  setFilterValue({
                    ...filterValue,
                    pro_loc: option
                      ? { value: `${option[0].value}`, method: 'boolean' }
                      : null,
                  })
                }
              />
            </InputContainer>
            <InputContainer>
              <InputLabel>Codice fiscale</InputLabel>
              <Input
                placeholder={'Inserisci'}
                dataCy={'code-input'}
                value={filterValue.codice_fiscale?.value || undefined}
                onChange={(data) =>
                  setFilterValue({
                    ...filterValue,
                    codice_fiscale:
                      { value: `${data}`, method: 'normal' } || null,
                  })
                }
              />
            </InputContainer>
          </FilterContent>
        </FilterContainer>
      </Container>
    </Card>
  )
}

//region Style
const Container = styled.div`
  display: grid;
  grid-template-columns: 0.5fr 3fr;
  gap: 20px;
`
const ButtonContainer = styled.div`
  place-self: center;
  height: 50px;
  width: 100%;
  display: grid;
`
const FilterContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  gap: 20px;
  border-left: 1px solid #ffffff;
`
const LabelContainer = styled.div`
  display: grid;
  padding: 0 15px;
  place-self: center start;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 1fr 4fr;
  grid-template-areas:
    'icon label'
    'icon count';
`
const IconContainer = styled.div`
  width: 17px;
  height: 17px;
  place-self: center start;
  grid-area: icon;
`

const CountLabel = styled.div`
  grid-area: count;
  place-self: center start;
  padding-top: 5px;
`
const Label = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  grid-area: label;
  place-self: center start;
`
const FilterContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 20px;
`

const InputContainer = styled.div`
  display: grid;
  grid-template-columns: 150px auto;
  gap: 10px;
`
const InputLabel = styled.div`
  place-self: center;
`
//endregion
